import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Star, X } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageUrl';

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    content: '',
    rating: 5,
    image: '',
    featured: true
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await api.get('/testimonials');
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/testimonials/${editingId}`, formData);
        toast.success('Testimonial updated');
      } else {
        await api.post('/admin/testimonials', formData);
        toast.success('Testimonial added');
      }
      setShowModal(false);
      resetForm();
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to save testimonial');
    }
  };

  const handleEdit = (testimonial) => {
    setFormData({
      name: testimonial.name,
      role: testimonial.role,
      content: testimonial.content,
      rating: testimonial.rating,
      image: testimonial.image || '',
      featured: testimonial.featured
    });
    setEditingId(testimonial.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      content: '',
      rating: 5,
      image: '',
      featured: true
    });
    setEditingId(null);
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-testimonials-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold">Testimonials</h1>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="add-testimonial-btn"
          >
            <Plus className="w-5 h-5" />
            Add Testimonial
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : testimonials.length === 0 ? (
          <div className="text-center py-12 glass-card">
            <p className="text-muted-foreground">No testimonials yet. Add your first one!</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {testimonials.map((t) => (
              <div key={t.id} className="glass-card p-6 flex items-start gap-4" data-testid={`testimonial-item-${t.id}`}>
                {t.image ? (
                  <img src={getImageUrl(t.image)} alt={t.name} className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center text-xl font-bold">
                    {t.name.charAt(0)}
                  </div>
                )}
                
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-semibold">{t.name}</h3>
                    <span className="text-sm text-muted-foreground">{t.role}</span>
                    {t.featured && (
                      <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">Featured</span>
                    )}
                  </div>
                  <div className="flex gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < t.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} />
                    ))}
                  </div>
                  <p className="text-muted-foreground">{t.content}</p>
                </div>

                <div className="flex gap-2">
                  <button onClick={() => handleEdit(t)} className="p-2 hover:bg-white/10 rounded-lg">
                    <Edit2 className="w-5 h-5 text-primary" />
                  </button>
                  <button onClick={() => handleDelete(t.id)} className="p-2 hover:bg-white/10 rounded-lg">
                    <Trash2 className="w-5 h-5 text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="glass-card p-6 w-full max-w-lg">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">
                  {editingId ? 'Edit Testimonial' : 'Add Testimonial'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">Role / Title *</label>
                  <input
                    type="text"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="e.g., Business Owner, Student"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">Testimonial Content *</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl resize-none"
                    rows={4}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">Rating</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setFormData({ ...formData, rating: r })}
                      >
                        <Star className={`w-8 h-8 ${r <= formData.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm mb-1">Image URL (optional)</label>
                  <input
                    type="url"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="https://..."
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <label htmlFor="featured">Featured on homepage</label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 btn-primary">
                    {editingId ? 'Update' : 'Add'} Testimonial
                  </button>
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 bg-white/10 rounded-full">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTestimonials;
